/**
 * Pattern Management View
 * View, edit, search, and manage operator patterns
 */

import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GitBranch,
  Search,
  Plus,
  Trash2,
  Edit,
  Copy,
  Play,
  ChevronDown,
  ChevronRight,
  Hash,
  Clock,
  TrendingUp,
  Zap,
  CheckCircle,
  MoreVertical,
  Loader2,
  X,
  RefreshCw,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { useDashboardStore } from "@/store";
import { convexService } from "@/services/convexService";

type PatternStatus = "active" | "evolving" | "deprecated" | "paused";

interface DisplayPattern {
  id: string;
  name: string;
  triggerHash: string;
  triggerValue: string;
  operators: string[];
  hitCount: number;
  successRate: number;
  avgLatency: number;
  confidence: number;
  fitness: number;
  status: PatternStatus;
  createdAt: Date;
  lastUsed: Date;
  tags: string[];
}

export function PatternsView() {
  const { settings, addLogEntry } = useDashboardStore();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<PatternStatus | "all">("all");
  const [sortBy, setSortBy] = useState<"hitCount" | "fitness" | "latency" | "recent">("hitCount");
  const [expandedPattern, setExpandedPattern] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newPattern, setNewPattern] = useState({
    name: "",
    triggerValue: "",
    tags: "",
  });
  const [isCreating, setIsCreating] = useState(false);

  // Mock data for now - replace with Convex calls
  const [patterns, setPatterns] = useState<DisplayPattern[]>([
    {
      id: "1",
      name: "Weather Query Pattern",
      triggerHash: "a1b2c3d4",
      triggerValue: "what's the weather",
      operators: ["weather-api", "parse-response", "format-output"],
      hitCount: 342,
      successRate: 0.98,
      avgLatency: 45,
      confidence: 0.92,
      fitness: 0.90,
      status: "active",
      createdAt: new Date("2024-12-20"),
      lastUsed: new Date("2024-12-28"),
      tags: ["weather", "api", "external"],
    },
    {
      id: "2",
      name: "Code Generation Pattern",
      triggerHash: "e5f6g7h8",
      triggerValue: "implement a function",
      operators: ["code-generator", "syntax-check", "test-generate"],
      hitCount: 189,
      successRate: 0.94,
      avgLatency: 120,
      confidence: 0.88,
      fitness: 0.85,
      status: "evolving",
      createdAt: new Date("2024-12-22"),
      lastUsed: new Date("2024-12-27"),
      tags: ["code", "generation", "qwen"],
    },
    {
      id: "3",
      name: "Math Proof Pattern",
      triggerHash: "i9j0k1l2",
      triggerValue: "prove that",
      operators: ["deepseek-reason", "verify-logic", "format-proof"],
      hitCount: 67,
      successRate: 0.91,
      avgLatency: 230,
      confidence: 0.85,
      fitness: 0.80,
      status: "evolving",
      createdAt: new Date("2024-12-24"),
      lastUsed: new Date("2024-12-26"),
      tags: ["math", "proof", "reasoning"],
    },
  ]);

  const handleCreatePattern = async () => {
    if (!newPattern.name || !newPattern.triggerValue) return;

    setIsCreating(true);
    setError(null);

    try {
      // Try to create in Convex
      if (convexService.isConfigured()) {
        await convexService.mutate("patterns:create", {
          name: newPattern.name,
          triggerValue: newPattern.triggerValue,
          triggerHash: btoa(newPattern.triggerValue).slice(0, 8),
          steps: [],
          confidence: 0.5,
          tags: newPattern.tags.split(",").map((t) => t.trim()).filter(Boolean),
        });
      }

      // Add to local state
      const newId = String(patterns.length + 1);
      setPatterns([
        ...patterns,
        {
          id: newId,
          name: newPattern.name,
          triggerHash: btoa(newPattern.triggerValue).slice(0, 8),
          triggerValue: newPattern.triggerValue,
          operators: [],
          hitCount: 0,
          successRate: 1.0,
          avgLatency: 0,
          confidence: 0.5,
          fitness: 0.5,
          status: "evolving",
          createdAt: new Date(),
          lastUsed: new Date(),
          tags: newPattern.tags.split(",").map((t) => t.trim()).filter(Boolean),
        },
      ]);

      addLogEntry({
        category: "pattern",
        action: "Pattern Created",
        details: `Created pattern "${newPattern.name}"`,
        severity: "info",
        source: "patterns-view",
      });

      setNewPattern({ name: "", triggerValue: "", tags: "" });
      setIsAddDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create pattern");
      addLogEntry({
        category: "pattern",
        action: "Pattern Creation Failed",
        details: err instanceof Error ? err.message : "Unknown error",
        severity: "error",
        source: "patterns-view",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeletePattern = async (patternId: string) => {
    try {
      if (convexService.isConfigured()) {
        await convexService.mutate("patterns:remove", { id: patternId });
      }

      setPatterns(patterns.filter((p) => p.id !== patternId));

      addLogEntry({
        category: "pattern",
        action: "Pattern Deleted",
        details: `Deleted pattern ${patternId}`,
        severity: "warning",
        source: "patterns-view",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete pattern");
    }
  };

  const filteredPatterns = useMemo(() => {
    let result = [...patterns];

    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(searchLower) ||
          p.triggerHash.toLowerCase().includes(searchLower) ||
          p.operators.some((op) => op.toLowerCase().includes(searchLower))
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((p) => p.status === statusFilter);
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "hitCount":
          return b.hitCount - a.hitCount;
        case "fitness":
          return b.fitness - a.fitness;
        case "latency":
          return a.avgLatency - b.avgLatency;
        case "recent":
          return b.lastUsed.getTime() - a.lastUsed.getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [patterns, search, statusFilter, sortBy]);

  const stats = useMemo(
    () => ({
      total: patterns.length,
      active: patterns.filter((p) => p.status === "active").length,
      evolving: patterns.filter((p) => p.status === "evolving").length,
      avgFitness:
        patterns.length > 0
          ? patterns.reduce((acc, p) => acc + p.fitness, 0) / patterns.length
          : 0,
    }),
    [patterns]
  );

  const handlePatternAction = (action: string, patternId: string) => {
    if (action === "delete") {
      handleDeletePattern(patternId);
      return;
    }

    addLogEntry({
      category: "pattern",
      action: `Pattern ${action}`,
      details: `Action "${action}" on pattern ${patternId}`,
      severity: action === "delete" ? "warning" : "info",
      source: "patterns-view",
    });
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg bg-red-500/20 p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Patterns" value={stats.total} icon={GitBranch} />
        <StatCard
          label="Active"
          value={stats.active}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          label="Evolving"
          value={stats.evolving}
          icon={TrendingUp}
          color="yellow"
        />
        <StatCard
          label="Avg Fitness"
          value={`${(stats.avgFitness * 100).toFixed(0)}%`}
          icon={Zap}
          color="purple"
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <input
            type="text"
            placeholder="Search patterns, operators, or hashes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-2 pl-10 pr-4 text-sm focus:border-[hsl(var(--primary))] focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as PatternStatus | "all")}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="evolving">Evolving</option>
            <option value="paused">Paused</option>
            <option value="deprecated">Deprecated</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm cursor-pointer"
          >
            <option value="hitCount">Most Used</option>
            <option value="fitness">Highest Fitness</option>
            <option value="latency">Fastest</option>
            <option value="recent">Recently Used</option>
          </select>

          <button
            disabled={isLoading}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-2 cursor-pointer hover:bg-[hsl(var(--muted))]"
            title="Refresh patterns"
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </button>

          <button
            onClick={() => setIsAddDialogOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-black cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            New Pattern
          </button>
        </div>
      </div>

      {/* Pattern List */}
      <div className="space-y-2">
        <AnimatePresence>
          {filteredPatterns.map((pattern) => (
            <PatternCard
              key={pattern.id}
              pattern={pattern}
              isExpanded={expandedPattern === pattern.id}
              onToggle={() =>
                setExpandedPattern(expandedPattern === pattern.id ? null : pattern.id)
              }
              onAction={handlePatternAction}
            />
          ))}
        </AnimatePresence>

        {filteredPatterns.length === 0 && (
          <div className="flex h-40 items-center justify-center text-[hsl(var(--muted-foreground))]">
            <div className="text-center">
              <Search className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p>No patterns found</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Pattern Dialog */}
      <Dialog.Root open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-semibold">
                Add New Pattern
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="rounded p-1 hover:bg-[hsl(var(--muted))] cursor-pointer">
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Pattern Name</label>
                <input
                  type="text"
                  value={newPattern.name}
                  onChange={(e) => setNewPattern({ ...newPattern, name: e.target.value })}
                  placeholder="e.g., Weather Query"
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:border-[hsl(var(--primary))] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Trigger Value</label>
                <input
                  type="text"
                  value={newPattern.triggerValue}
                  onChange={(e) => setNewPattern({ ...newPattern, triggerValue: e.target.value })}
                  placeholder="e.g., what's the weather"
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:border-[hsl(var(--primary))] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={newPattern.tags}
                  onChange={(e) => setNewPattern({ ...newPattern, tags: e.target.value })}
                  placeholder="e.g., weather, api, external"
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:border-[hsl(var(--primary))] focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Dialog.Close asChild>
                <button className="rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm cursor-pointer hover:bg-[hsl(var(--muted))]">
                  Cancel
                </button>
              </Dialog.Close>
              <button
                onClick={handleCreatePattern}
                disabled={isCreating || !newPattern.name || !newPattern.triggerValue}
                className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-black cursor-pointer disabled:opacity-50"
              >
                {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Pattern
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color = "default",
}: {
  label: string;
  value: string | number;
  icon: typeof GitBranch;
  color?: "default" | "green" | "yellow" | "purple" | "red";
}) {
  const colorClasses = {
    default: "text-[hsl(var(--foreground))]",
    green: "text-green-400",
    yellow: "text-yellow-400",
    purple: "text-purple-400",
    red: "text-red-400",
  };

  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">{label}</span>
        <Icon className={cn("h-4 w-4", colorClasses[color])} />
      </div>
      <p className={cn("mt-2 text-2xl font-bold", colorClasses[color])}>{value}</p>
    </div>
  );
}

function PatternCard({
  pattern,
  isExpanded,
  onToggle,
  onAction,
}: {
  pattern: DisplayPattern;
  isExpanded: boolean;
  onToggle: () => void;
  onAction: (action: string, id: string) => void;
}) {
  const statusColors = {
    active: "bg-green-500/20 text-green-400 border-green-500/30",
    evolving: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    paused: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    deprecated: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden"
    >
      <div className="flex cursor-pointer items-center gap-4 p-4" onClick={onToggle}>
        <button className="text-[hsl(var(--muted-foreground))] cursor-pointer">
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="font-medium">{pattern.name}</h3>
            <span className={cn("rounded-full border px-2 py-0.5 text-xs", statusColors[pattern.status])}>
              {pattern.status}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))]">
            <span className="flex items-center gap-1">
              <Hash className="h-3 w-3" />
              {pattern.triggerHash}
            </span>
            <span className="flex items-center gap-1">
              <Zap className="h-3 w-3" />
              {pattern.hitCount} hits
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {pattern.avgLatency}ms
            </span>
          </div>
        </div>

        <div className="flex items-center gap-6 text-sm">
          <div className="text-center">
            <p className="text-[hsl(var(--muted-foreground))] text-xs">Success</p>
            <p className={pattern.successRate > 0.9 ? "text-green-400" : "text-yellow-400"}>
              {(pattern.successRate * 100).toFixed(0)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-[hsl(var(--muted-foreground))] text-xs">Fitness</p>
            <p className={pattern.fitness > 0.8 ? "text-green-400" : "text-yellow-400"}>
              {(pattern.fitness * 100).toFixed(0)}%
            </p>
          </div>
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button onClick={(e) => e.stopPropagation()} className="rounded p-1 hover:bg-[hsl(var(--muted))] cursor-pointer">
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="min-w-[160px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1 shadow-lg" sideOffset={5}>
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm hover:bg-[hsl(var(--muted))]" onClick={() => onAction("edit", pattern.id)}>
                <Edit className="h-4 w-4" /> Edit
              </DropdownMenu.Item>
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm hover:bg-[hsl(var(--muted))]" onClick={() => onAction("duplicate", pattern.id)}>
                <Copy className="h-4 w-4" /> Duplicate
              </DropdownMenu.Item>
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm hover:bg-[hsl(var(--muted))]" onClick={() => onAction("test", pattern.id)}>
                <Play className="h-4 w-4" /> Test Run
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-[hsl(var(--border))]" />
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-red-400 hover:bg-red-500/20" onClick={() => onAction("delete", pattern.id)}>
                <Trash2 className="h-4 w-4" /> Delete
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-[hsl(var(--border))] bg-[hsl(var(--muted)/.3)]"
          >
            <div className="p-4 space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Operator Chain</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  {pattern.operators.map((op, i) => (
                    <span key={op} className="rounded bg-[hsl(var(--primary)/.2)] px-2 py-1 text-xs font-mono text-[hsl(var(--primary))]">
                      {op}
                      {i < pattern.operators.length - 1 && <span className="ml-2 text-[hsl(var(--muted-foreground))]">→</span>}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[hsl(var(--muted-foreground))]">Created</p>
                  <p>{pattern.createdAt.toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-[hsl(var(--muted-foreground))]">Last Used</p>
                  <p>{pattern.lastUsed.toLocaleDateString()}</p>
                </div>
              </div>
              {pattern.tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Tags</h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    {pattern.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-[hsl(var(--muted))] px-2 py-0.5 text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
